import { TrendingUp, Star, Users, Globe2 } from "lucide-react";

const results = [
  {
    org: "AAO",
    detail: "American Association of Orthodontists",
    metric: "5×+",
    label: "Annual Meetings Presented",
    icon: Star,
  },
  {
    org: "PCSO 1998",
    detail: "Pacific Coast Society of Orthodontists",
    metric: "90%",
    label: "Rated 'Excellent' · 250+ attendees",
    icon: TrendingUp,
  },
  {
    org: "Pacific NW Dental 2008",
    detail: "Pacific Northwest Dental Conference",
    metric: "4.9 / 5",
    label: "Two programs · 8,500 attendees",
    icon: Star,
  },
  {
    org: "RDH Under One Roof 2007",
    detail: "RDH Magazine Keynote",
    metric: "★★★★★",
    label: "Reviews described as 'off the charts'",
    icon: TrendingUp,
  },
  {
    org: "A-dec",
    detail: "Corporate Sales Training",
    metric: "4.3 / 5",
    label: "Internal evaluation score",
    icon: Star,
  },
  {
    org: "AACD",
    detail: "American Academy of Cosmetic Dentistry",
    metric: "22nd",
    label: "Annual Scientific Session presenter",
    icon: Users,
  },
  {
    org: "Chicago Midwinter",
    detail: "Chicago Dental Society",
    metric: "★★★★",
    label: "Rated 'Very Good to Excellent'",
    icon: Star,
  },
  {
    org: "UOP",
    detail: "University of the Pacific Arthur A. Dugoni",
    metric: "Recurring",
    label: "Invited speaker (multi-year)",
    icon: Users,
  },
];

const referralWins = [
  {
    practice: "OMSC of Minnesota",
    headline: "300+ attendees · Booked 5×+",
    detail:
      "Oral and Maxillofacial Surgery Consultants — the largest oral surgery practice in Minnesota. Gary has been brought back five times to deliver referral appreciation and team development events drawing 300+ attendees.",
    quote: "— Repeat Client · 5× Booked",
  },
  {
    practice: "Hatcher Orthodontics",
    headline: "Encore booking due to demand",
    detail:
      "Seminar for referring offices. Attendees refused to leave during break — afraid of missing something.",
    quote: "— Dr. William R. Hatcher",
  },
  {
    practice: "Mark S. Douglas, DDS, MSD",
    headline: "Best year ever",
    detail:
      "Referral luncheon + team-building reinvigorated the entire practice and external partner network.",
    quote: "— Dr. Mark S. Douglas",
  },
  {
    practice: "Bartley H. Benson, DDS, MS",
    headline: "Repeat booking · high attendance",
    detail:
      "Referral-source seminar with strong turnout; brought Gary back for follow-up engagements.",
    quote: "— Dr. Bartley H. Benson",
  },
];

export default function Results() {
  return (
    <section
      id="results"
      data-testid="results-section"
      className="bg-[#050505] py-24 md:py-32 border-t border-[#262626]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="section-num">08 / VERIFIED RESULTS</span>
          <div className="divider-red" />
        </div>
        <h2 className="h-display text-5xl md:text-6xl lg:text-7xl max-w-4xl mb-6">
          Receipts, not <br />
          <span className="text-[#EC1985]">rhetoric.</span>
        </h2>
        <p className="text-white/70 text-lg max-w-2xl mb-14">
          Real ratings, real attendee counts, real return engagements from the organizations
          who hire Gary year after year.
        </p>

        {/* Results grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {results.map((r, i) => {
            const Icon = r.icon;
            return (
              <div
                key={i}
                data-testid={`result-${i}`}
                className="surface p-6 hover:border-[#EC1985] transition-colors"
              >
                <Icon size={20} className="text-[#EC1985] mb-4" />
                <div className="font-display text-4xl md:text-5xl text-white leading-none">
                  {r.metric}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.15em] font-bold text-white/85">
                  {r.org}
                </div>
                <div className="mt-1 text-xs text-white/55">{r.detail}</div>
                <div className="mt-3 pt-3 border-t border-[#262626] text-xs text-white/70">
                  {r.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Referral case studies */}
        <div className="border-t border-[#262626] pt-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="section-num">CAUSE FOR APPLAUSE · TRACK RECORD</span>
            <div className="divider-red" />
          </div>
          <h3 className="h-display text-4xl md:text-5xl text-white mb-12 max-w-3xl">
            Referral relationships,<br />
            <span className="text-[#EC1985]">measurably stronger.</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {referralWins.map((r, i) => (
              <article
                key={i}
                data-testid={`referral-${i}`}
                className="surface p-8 hover:border-[#EC1985] transition-colors"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-[#EC1985] font-bold">
                  {r.practice}
                </div>
                <div className="mt-3 font-display text-2xl md:text-3xl text-white">
                  {r.headline}
                </div>
                <p className="mt-4 text-white/70 leading-relaxed">{r.detail}</p>
                <div className="mt-4 text-xs text-white/50 uppercase tracking-[0.15em] font-bold">
                  {r.quote}
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* International reach */}
        <div className="mt-20 border-t border-[#262626] pt-16 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Globe2 size={20} className="text-[#EC1985]" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-white/70">
                International Engagements
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white mb-6">
              Spoken in <span className="text-[#EC1985]">5+ countries</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {["United States", "Canada", "United Kingdom", "London", "Ireland", "Bahamas"].map(
                (c) => (
                  <span
                    key={c}
                    className="text-xs uppercase tracking-[0.15em] font-bold border border-[#262626] px-3 py-2 text-white/85"
                  >
                    {c}
                  </span>
                ),
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-6">
              <Users size={20} className="text-[#EC1985]" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-white/70">
                Specialty Practice Experience
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white mb-6">
              Repeat clients across <span className="text-[#EC1985]">every specialty</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {[
                "Orthodontic",
                "Oral Surgery",
                "Endodontic",
                "Implant Dentistry",
                "Pediatric Dental",
                "Cosmetic",
                "General Practice",
              ].map((c) => (
                <span
                  key={c}
                  className="text-xs uppercase tracking-[0.15em] font-bold border border-[#262626] px-3 py-2 text-white/85"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
